import React from 'react';
import { TrendingUp, TrendingDown, Wallet, Flame, Activity, AlertCircle, AlertTriangle, Info, ArrowUpRight, ArrowDownRight } from 'lucide-react';
import { formatCurrency } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { CustomTooltip } from './ReportHelpers';

function ChangeBadge({ pct }) {
  if (pct === null || pct === undefined) return <span className="text-xs text-zinc-400">N/A</span>;
  const up = pct > 0;
  const color = up ? 'text-red-600 bg-red-50' : 'text-emerald-600 bg-emerald-50';
  const icon = up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />;
  return <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium ${color}`}>{icon}{Math.abs(pct)}%</span>;
}

function RevenueChangeBadge({ pct }) {
  if (pct === null || pct === undefined) return <span className="text-xs text-zinc-400">N/A</span>;
  const up = pct > 0;
  const color = up ? 'text-emerald-600 bg-emerald-50' : 'text-red-600 bg-red-50';
  const icon = up ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />;
  return <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium ${color}`}>{icon}{Math.abs(pct)}%</span>;
}

export function HeartbeatSection({ heartbeatData }) {
  if (!heartbeatData) return null;
  const d = heartbeatData;
  const mom = d.month_vs_month;
  const h1 = d.h1_comparison;

  const MetricCard = ({ label, value, sub, icon: Icon, color = 'zinc' }) => (
    <div className="bg-white border border-zinc-200 rounded-xl p-5" data-testid={`hb-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium text-zinc-500 uppercase tracking-wide">{label}</span>
        {Icon && <Icon className={`w-4 h-4 text-${color}-500`} />}
      </div>
      <p className="text-2xl font-bold text-zinc-900 tabular-nums">{value}</p>
      {sub && <p className="text-xs text-zinc-500 mt-1">{sub}</p>}
    </div>
  );

  const alertIcon = (s) => s === 'critical' ? <AlertCircle className="w-4 h-4 text-red-500" /> : s === 'warning' ? <AlertTriangle className="w-4 h-4 text-amber-500" /> : <Info className="w-4 h-4 text-blue-500" />;
  const alertBg = (s) => s === 'critical' ? 'bg-red-50 border-red-200' : s === 'warning' ? 'bg-amber-50 border-amber-200' : 'bg-blue-50 border-blue-200';

  const momRows = [
    { label: 'Revenue', cur: mom.revenue.current, prev: mom.revenue.previous, pct: mom.revenue.change_pct, isRevenue: true },
    { label: 'Expenses', cur: mom.expenses.current, prev: mom.expenses.previous, pct: mom.expenses.change_pct },
    { label: 'Net Income', cur: mom.net_income.current, prev: mom.net_income.previous, pct: mom.net_income.change_pct, isRevenue: true },
  ];

  return (
    <div className="space-y-6" data-testid="heartbeat-panel">
      <div className="flex items-center gap-3">
        <Activity className="w-5 h-5 text-[#D94E18]" />
        <div>
          <h2 className="text-lg font-bold text-zinc-900">Company Heartbeat</h2>
          <p className="text-xs text-zinc-500">As of {d.as_of}</p>
        </div>
      </div>

      {d.alerts.length > 0 && (
        <div className="space-y-2" data-testid="hb-alerts">
          {d.alerts.map((a, idx) => (
            <div key={a.message || `hb-alert-${idx}`} className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${alertBg(a.severity)}`}>
              {alertIcon(a.severity)}
              <span className="text-sm font-medium">{a.message}</span>
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Cash Balance" value={formatCurrency(d.cash_balance)} icon={Wallet} color="emerald" />
        <MetricCard
          label="Cash Runway"
          value={d.cash_runway_months !== null ? `${d.cash_runway_months} months` : 'Healthy'}
          sub={d.cash_runway_months !== null ? `Avg burn ${formatCurrency(d.avg_monthly_burn)}/mo` : 'Cash is growing'}
          icon={Flame}
          color={d.cash_runway_months !== null && d.cash_runway_months < 6 ? 'red' : 'emerald'}
        />
        <MetricCard
          label="Expenses This Month"
          value={formatCurrency(mom.expenses.current)}
          sub={<>vs {formatCurrency(mom.expenses.previous)} last month <ChangeBadge pct={mom.expenses.change_pct} /></>}
          icon={TrendingDown}
          color="red"
        />
        <MetricCard
          label="Revenue This Month"
          value={formatCurrency(mom.revenue.current)}
          sub={<>vs {formatCurrency(mom.revenue.previous)} last month <RevenueChangeBadge pct={mom.revenue.change_pct} /></>}
          icon={TrendingUp}
          color="emerald"
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">{mom.current_month} vs {mom.previous_month}</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm">
              <thead><tr className="border-b border-zinc-200"><th className="text-left py-2 text-zinc-500 font-medium">Metric</th><th className="text-right py-2 text-zinc-500 font-medium">{mom.current_month}</th><th className="text-right py-2 text-zinc-500 font-medium">{mom.previous_month}</th><th className="text-right py-2 text-zinc-500 font-medium">Change</th></tr></thead>
              <tbody>
                {momRows.map(r => (
                  <tr key={r.label} className="border-b border-zinc-100">
                    <td className="py-2.5 font-medium text-zinc-700">{r.label}</td>
                    <td className="py-2.5 text-right font-mono tabular-nums">{formatCurrency(r.cur)}</td>
                    <td className="py-2.5 text-right font-mono tabular-nums text-zinc-400">{formatCurrency(r.prev)}</td>
                    <td className="py-2.5 text-right">{r.isRevenue ? <RevenueChangeBadge pct={r.pct} /> : <ChangeBadge pct={r.pct} />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold">Top Movers (Expense Categories)</CardTitle>
          </CardHeader>
          <CardContent>
            {d.top_movers.length > 0 ? (
              <div className="space-y-3">
                {d.top_movers.slice(0, 6).map(m => (
                  <div key={m.name} className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-zinc-700">{m.name}</p>
                      <p className="text-xs text-zinc-400">{formatCurrency(m.previous)} → {formatCurrency(m.current)}</p>
                    </div>
                    <ChangeBadge pct={m.pct_change} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-zinc-400">No category changes this period</p>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">Last 12 Months</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={d.last_12_months} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `${(v / 1000).toFixed(0)}k`} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="revenue" name="Revenue" fill="#34D399" radius={[3, 3, 0, 0]} />
                <Bar dataKey="expenses" name="Expenses" fill="#D94E18" radius={[3, 3, 0, 0]} />
                <Legend />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold">H1 {h1.this_year} vs H1 {h1.last_year} (Jan — Jun)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <p className="text-xs text-zinc-500 uppercase tracking-wide mb-3">H1 {h1.this_year}</p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-zinc-600">Revenue</span><span className="font-mono font-medium">{formatCurrency(h1.this_h1.revenue)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-zinc-600">Expenses</span><span className="font-mono font-medium">{formatCurrency(h1.this_h1.expenses)}</span></div>
                <div className="flex justify-between text-sm border-t pt-2"><span className="font-medium text-zinc-700">Net</span><span className={`font-mono font-bold ${h1.this_h1.net >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{formatCurrency(h1.this_h1.net)}</span></div>
              </div>
            </div>
            <div>
              <p className="text-xs text-zinc-500 uppercase tracking-wide mb-3">H1 {h1.last_year}</p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-zinc-600">Revenue</span><span className="font-mono font-medium text-zinc-400">{formatCurrency(h1.last_h1.revenue)}</span></div>
                <div className="flex justify-between text-sm"><span className="text-zinc-600">Expenses</span><span className="font-mono font-medium text-zinc-400">{formatCurrency(h1.last_h1.expenses)}</span></div>
                <div className="flex justify-between text-sm border-t pt-2"><span className="font-medium text-zinc-700">Net</span><span className="font-mono font-bold text-zinc-400">{formatCurrency(h1.last_h1.net)}</span></div>
              </div>
            </div>
          </div>
          <div className="mt-4 flex gap-4 text-xs">
            {h1.expense_change_pct !== null && <span className="text-zinc-500">Expenses: <ChangeBadge pct={h1.expense_change_pct} /> YoY</span>}
            {h1.revenue_change_pct !== null && <span className="text-zinc-500">Revenue: <RevenueChangeBadge pct={h1.revenue_change_pct} /> YoY</span>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
